"use client"

import type React from "react"
import { ArrowLeft, ArrowRight, Focus, Images, Loader2, Lock, Plus, Sun, SunDim, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { analytics } from "@/lib/analytics"
import type { AnalysisMode as SharedAnalysisMode } from "@/lib/types/diagnosis"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { AnalysisLawnChoice } from "@/components/home/analysis-lawn-choice"
import { PhotoQualityWarning } from "@/components/home/photo-quality-warning"
import type { PhotoQualityReason } from "@/lib/analysis/photo-quality"
import type { PhotoQualityWarning as PhotoQualityWarningValue, SupportingAnalysisImage } from "@/hooks/use-homepage-analysis"
import type { UserLawn } from "@/hooks/use-default-lawn"
import { useVisibleImpression } from "@/hooks/use-visible-impression"

type AnalysisMode = Exclude<SharedAnalysisMode, 'weed'>

const SUPPORTING_QUALITY_ICON = {
  blur: Focus,
  dark: Sun,
  bright: SunDim,
} as const

const SUPPORTING_QUALITY_LABEL: Record<PhotoQualityReason, string> = {
  blur: "Looks blurry",
  dark: "Looks dark",
  bright: "Looks washed out",
}

/** One delivered impression per attempt, module-scoped so a remount inside
 *  the same attempt (mode toggle, error retry) does not double-count. */
const zipStepImpressionKeys = new Set<string>()

interface ZipCodeStepProps {
  zipCode: string
  zipCodeError: string
  isZipCodeValid: boolean
  uploadedImage: string
  onZipCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onZipCodeKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onAnalyze: () => void
  onReset?: () => void
  mode?: AnalysisMode
  supportingImages?: SupportingAnalysisImage[]
  isUploadingSupportingImages?: boolean
  supportingImageError?: string
  onSupportingImagesUpload?: (files: File[]) => void
  onRemoveSupportingImage?: (url: string) => void
  /** Post-result refinement lane. Supporting photos are only ever collected
   *  here; the first analysis is single-photo for every tier. */
  isRefinementStep?: boolean
  requireSupportingPhoto?: boolean
  supportingPhotoGuidance?: string | null
  /** Anonymous visitor: the slots render, but the picker is replaced by a
   *  sign-in affordance. The server rejects a multi-photo anonymous request,
   *  so an open picker here would only produce an error later. */
  supportingPhotosLocked?: boolean
  onSupportingPhotosSignIn?: () => void
  /** Total photos this visitor may send, primary included. */
  photoCap?: number
  lawns?: UserLawn[]
  lawnChoice?: string | null
  lawnChoiceRequired?: boolean
  lawnChoiceMade?: boolean
  onLawnChoice?: (lawnId: string | null) => void
  /** Pre-call capture warning for the primary photo. Advisory: it never gates
   *  the analyze button. */
  photoQuality?: PhotoQualityWarningValue | null
  /** Supporting-photo warnings, keyed by uploaded URL. */
  supportingPhotoQuality?: Record<string, PhotoQualityReason>
  onQualityRetake?: () => void
  funnelId?: string | null
  attemptId?: string | null
  authState?: 'anonymous' | 'authenticated' | 'unknown'
  className?: string
}

export function ZipCodeStep({
  zipCode,
  zipCodeError,
  isZipCodeValid,
  uploadedImage,
  onZipCodeChange,
  onZipCodeKeyPress,
  onAnalyze,
  onReset,
  mode = "identify",
  supportingImages = [],
  isUploadingSupportingImages = false,
  supportingImageError = "",
  onSupportingImagesUpload,
  onRemoveSupportingImage,
  isRefinementStep = false,
  requireSupportingPhoto = false,
  supportingPhotoGuidance,
  supportingPhotosLocked = false,
  onSupportingPhotosSignIn,
  photoCap = 3,
  lawns = [],
  lawnChoice,
  lawnChoiceRequired = false,
  lawnChoiceMade = true,
  onLawnChoice,
  photoQuality = null,
  supportingPhotoQuality = {},
  onQualityRetake,
  funnelId = null,
  attemptId = null,
  authState = 'unknown',
  className,
}: ZipCodeStepProps) {
  const isDiagnose = mode === "diagnose"
  // The panel sits above the ZIP because its photo is required to analyze.
  const showRefinementPanel = isRefinementStep && Boolean(onSupportingImagesUpload)
  // A locked visitor's cap is 1, so the slot math alone would hide the panel's
  // only affordance. Keep exactly one slot visible: it is what the sign-in ask
  // is about.
  const supportingSlotCount = Math.max(supportingPhotosLocked ? 1 : 0, photoCap - 1)
  const canAddSupportingPhoto = supportingImages.length < supportingSlotCount
  const canAnalyze = Boolean(
    isZipCodeValid &&
    zipCode &&
    !isUploadingSupportingImages &&
    (!lawnChoiceRequired || lawnChoiceMade) &&
    (!requireSupportingPhoto || supportingImages.length > 0),
  )

  // Bridge string-based ZipCodeInput onChange into the parent's
  // ChangeEvent-based handler so existing callers don't need updating.
  const handleZipChange = (zip: string, meta?: { entryMethod?: 'typed' | 'saved' | 'geolocation' }) => {
    const synthetic = {
      target: { value: zip },
      entryMethod: meta?.entryMethod,
    } as React.ChangeEvent<HTMLInputElement> & { entryMethod?: 'typed' | 'saved' | 'geolocation' }
    onZipCodeChange(synthetic)
  }

  // Submit on Enter when the ZIP is valid. The shared input owns its own
  // keydown, so wire this through a wrapper.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      onZipCodeKeyPress(e as unknown as React.KeyboardEvent<HTMLInputElement>)
    }
  }

  // attemptId is read off a ref upstream, so it can reach this step a render
  // after it first paints. An impression emitted before the attempt id exists
  // carries attempt_id null and joins nothing, so hold it until the id lands.
  // TRUE step impression: fires for every visitor who reaches the ZIP step,
  // deduped on `${attemptId}:${mode}`.
  useVisibleImpression(
    zipStepImpressionKeys,
    Boolean(attemptId),
    `${attemptId}:${mode}`,
    () => {
      analytics.zipStepShown({
        mode,
        attemptId,
        funnelId,
        authState,
        isRefinementStep,
        photoCap,
      })
    },
  )

  const supportingPanel = (
    <div className="mx-auto max-w-md rounded-2xl border border-green-100 bg-green-50/50 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-green-700 ring-1 ring-green-100">
          <Images className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {supportingPhotosLocked
              ? "Add a second view, free account"
              : "Add one more view to refine this result"}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-gray-600">
            {supportingPhotoGuidance || (isDiagnose
              ? "Add a close-up of the damaged blades or the edge between healthy and affected grass."
              : "Add a blade close-up or a wider view of how the grass grows across the lawn.")}
          </p>
        </div>
      </div>

      {/* Recognition over recall: the primary photo leads the strip as filled
          slot 1, so "one done, one to go" reads as images rather than as a
          sentence. The dashed tile is the targeted view we are asking for. */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="relative overflow-hidden rounded-xl border border-green-100 bg-white">
          <img
            src={uploadedImage}
            alt="Your first lawn photo"
            className="ph-no-capture h-24 w-full object-cover"
            data-ph-no-capture="true"
          />
          <span className="absolute left-1.5 top-1.5 inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-green-700">
            1
          </span>
        </div>
        {supportingImages.map((image, index) => {
          const supportingReason = supportingPhotoQuality[image.url]
          const SupportingIcon = supportingReason ? SUPPORTING_QUALITY_ICON[supportingReason] : null
          return (
          <div key={image.url} className="relative overflow-hidden rounded-xl border border-green-100 bg-white">
            <img
              src={image.preview}
              alt={`Supporting lawn view ${index + 1}`}
              className="h-24 w-full object-cover"
            />
            {supportingReason && SupportingIcon && (
              <span
                data-testid={`supporting-quality-${image.url}`}
                className="absolute inset-x-0 bottom-0 inline-flex items-center gap-1 bg-amber-50/95 px-1.5 py-1 text-[10px] font-semibold text-amber-800"
              >
                <SupportingIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                {SUPPORTING_QUALITY_LABEL[supportingReason]}
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemoveSupportingImage?.(image.url)}
              className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-950/75 text-white hover:bg-gray-950 focus:outline-hidden focus:ring-2 focus:ring-white"
              aria-label={`Remove supporting view ${index + 1}`}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
          )
        })}
        {canAddSupportingPhoto && (
          <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-green-300 bg-white/60 text-green-600">
            {supportingPhotosLocked ? (
              <Lock className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Plus className="h-4 w-4" aria-hidden="true" />
            )}
          </div>
        )}
      </div>

      {canAddSupportingPhoto && (supportingPhotosLocked ? (
        <button
          type="button"
          onClick={onSupportingPhotosSignIn}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-green-200 bg-white px-4 py-2.5 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50 focus:outline-hidden focus:ring-2 focus:ring-green-500/30"
        >
          <Lock className="h-4 w-4" aria-hidden="true" />
          Add a second photo, free account
        </button>
      ) : (
        <label className="mt-3 inline-flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-green-200 bg-white px-4 py-2.5 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50 focus-within:ring-2 focus-within:ring-green-500/30">
          {isUploadingSupportingImages ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="h-4 w-4" aria-hidden="true" />
          )}
          {isUploadingSupportingImages ? "Adding photos..." : "Add recommended photo"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            disabled={isUploadingSupportingImages}
            className="sr-only"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? [])
              if (files.length > 0) onSupportingImagesUpload?.(files)
              event.target.value = ""
            }}
          />
        </label>
      ))}
      {supportingImageError && (
        <p role="alert" className="mt-2 text-xs leading-relaxed text-red-600">{supportingImageError}</p>
      )}
      {requireSupportingPhoto && supportingImages.length === 0 && !supportingImageError && (
        <p className="mt-2 text-xs leading-relaxed text-gray-500">
          Your original photo is saved. Add this view before running the refinement.
        </p>
      )}
    </div>
  )

  return (
    <div className={cn("text-left", className)}>
      {onReset && (
        <div className="text-center sm:text-left">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 min-h-[44px] rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Use a different photo
          </button>
        </div>
      )}

      <div className="flex flex-col items-center mt-4 mb-5 sm:mt-7 sm:mb-7">
        <img
          src={uploadedImage}
          alt="Your grass photo"
          className="ph-no-capture w-40 h-40 sm:w-56 sm:h-56 object-cover rounded-2xl shadow-xs"
          data-ph-no-capture="true"
        />
      </div>

      {photoQuality && onQualityRetake && (
        <div className="mb-6">
          <PhotoQualityWarning
            warning={photoQuality}
            mode={mode}
            funnelId={funnelId}
            authState={authState}
            onRetake={onQualityRetake}
          />
        </div>
      )}

      {showRefinementPanel && <div className="mb-6">{supportingPanel}</div>}

      <div className="max-w-md mx-auto">
        {/* leading-[1.2] gives wrapped lines room so the accent underline under
            one line never clips into the next; a text-decoration underline with
            offset flows with the line box better than a border-b. */}
        {/* No programmatic focus target here on purpose: the ZipCodeInput
            below carries autoFocus, so the step transition already moves focus
            into the new step. */}
        <h2 className="text-center text-3xl md:text-4xl font-bold text-gray-900 leading-[1.2] tracking-tight mb-3">
          Where&rsquo;s{" "}
          <span className="text-green-600 underline decoration-[3px] decoration-green-600 underline-offset-4">
            your lawn?
          </span>
        </h2>
        <p className="text-center text-[15px] text-gray-500 leading-relaxed mb-5 sm:mb-7">
          {isDiagnose
            ? "ZIP helps us match regional diseases and pests likely in your area."
            : "ZIP narrows the candidates to grass types that actually grow in your climate, which improves accuracy."}
        </p>

        <div className="mb-5" onKeyDown={handleKeyDown}>
          <ZipCodeInput
            id="zip-input-results"
            value={zipCode}
            onChange={handleZipChange}
            error={zipCodeError || undefined}
            geolocationVariant="button"
            autoFocus
            rememberLastZip
          />
        </div>

        {lawnChoiceRequired && onLawnChoice ? (
          <AnalysisLawnChoice
            lawns={lawns}
            choice={lawnChoiceMade ? lawnChoice : undefined}
            onChange={onLawnChoice}
            mode={mode}
          />
        ) : null}

        <button
          type="button"
          onClick={onAnalyze}
          disabled={!canAnalyze}
          data-testid="analyze-button"
          className={cn(
            "w-full inline-flex items-center justify-center gap-2 py-4 rounded-[10px] text-base font-semibold transition-colors",
            canAnalyze
              ? "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
              : "bg-gray-100 text-gray-400 cursor-not-allowed",
          )}
        >
          {isDiagnose ? "Diagnose problem" : isRefinementStep ? "Refine my result" : "Identify my grass"}
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </button>

        <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-gray-500">
          <Lock className="w-3 h-3" />
          Used to match your climate. Never shared.
        </div>
      </div>
    </div>
  )
}
